import * as React from 'react'
import { WithTree } from './navigationHelpers/WithTree'
import type { ILeaf, INode } from 'shared/types/tree'
import Typography from '@mui/material/Typography'
import Container from '@mui/material/Container'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CircularProgress from '@mui/material/CircularProgress'
import { allLeavesOfType } from '../utility/TreeHelpers'
import { LeafTypes, NodeType } from 'shared/types/ingestion'
import CardHeader from '@mui/material/CardHeader'
import { Button, CardActions, TextField } from '@mui/material'
import { exportCsv } from 'shared/utils'
import { createIdMap, NodeIdMap } from '../version_mapping/utility'
import { getStandardsByIds } from 'shared/hooks/standards/hooks'
import { useSubjectsByName } from 'shared/api/subjects'

export function ExportStandardsContainer() {
  return <WithTree Wrapped={ExportStandards} />
}

const ancestorHeaders = ['Unit', 'Lesson Plan', 'Activity/Assessment']
const headers = [
  'Item Number',
  ...ancestorHeaders,
  'Item Title',
  'Item Description',
  'Item Grade',
  'State',
  'Adoption Date',
  'Standard Subject',
  'Standard ID',
  'Standard Text',
  'Standard Grade',
]

const formatStringForCSV = (str?: string) =>
  str ? `"${str.replace(/"/g, '""')}"` : ''

function ancestorTitles(node: INode, nodeMap: NodeIdMap) {
  const ancestors = getAncestors(node, nodeMap)
  return [
    ancestors.find(({ type }) => type === NodeType.Unit)?.title ?? '',
    ancestors.find(({ type }) => type === NodeType.LessonPlan)?.title ?? '',
    ancestors.find(({ type }) =>
      [NodeType.Activity, NodeType.Assessment].includes(type as any),
    )?.title ?? '',
  ].map(formatStringForCSV)
}

export function ExportStandards({ tree }: { tree: INode }) {
  const subjects = useSubjectsByName() ?? {}
  const [loading, setLoading] = React.useState(false)
  const [parsedStandards, setParsedStandards] = React.useState<
    string[][] | null
  >(null)

  const [grade, setGrade] = React.useState('')
  const [state, setState] = React.useState('')
  const [treeMap, setTreeMap] = React.useState<NodeIdMap>({})

  const exportStandardsAsCSV = () => {
    if (parsedStandards) {
      exportCsv([headers, ...parsedStandards])
    }
  }

  const grandparentMap = new Map<number, INode[]>()

  const mapGrandparents = (tree: INode, skill: ILeaf) => {
    const grandparent =
      treeMap[treeMap?.[skill?.parent_id ?? '']?.node.parent_id].node

    if (grandparent) {
      const existing = grandparentMap.get(skill.body.skillID) ?? []
      grandparentMap.set(
        skill.body.skillID,
        Array.from(new Set([...existing, grandparent])),
      )
    }
  }
  async function parseStandards() {
    setLoading(true)
    const standardIDs = new Set(
      allLeavesOfType(tree, LeafTypes.Skill).reduce<string[]>((acc, skill) => {
        if (skill?.body?.skillID) {
          mapGrandparents(tree, skill)
          return [...acc, `${skill.body.skillID}`]
        }
        return acc
      }, [] as string[]),
    )
    return await requestSkillsForIds(...Array.from(standardIDs))
  }
  async function requestSkillsForIds(...ids: (number | string)[]) {
    const subjectMap = new Map()
    Object.entries(subjects).forEach(([key, value]) => {
      subjectMap.set(value.subject_id, key)
    })
    const { standards } = await getStandardsByIds(ids)
    const sortColumn = headers.indexOf('Standard ID')
    setParsedStandards(
      standards
        .reduce<string[][]>((acc, standard) => {
          const grandparents = grandparentMap.get(standard.standardId)
          const mapped: string[][] =
            grandparents?.map((grandparent: any) => {
              return [
                '',
                ...ancestorTitles(grandparent, treeMap),
                formatStringForCSV(grandparent.title),
                formatStringForCSV(grandparent.type),
                formatStringForCSV(grade),
                formatStringForCSV(state),
                '',
                formatStringForCSV(subjectMap.get(standard.subjectId)),
                formatStringForCSV(standard.standardName),
                formatStringForCSV(standard.standardDescription),
                `${standard.gradeId - 1 || 'K'}`,
              ]
            }) ?? ([] as string[][])
          return [...acc, ...mapped]
        }, [])
        .sort((a, b) => a[sortColumn].localeCompare(b[sortColumn])),
    )
    setLoading(false)
  }

  React.useEffect(() => {
    // Reset on new tree
    setTreeMap(createIdMap(tree))
    setGrade('')
    setState('')
  }, [tree])

  return (
    <>
      <Typography variant="h4" component="h2" color="initial">
        Export Standards
      </Typography>
      <br />
      {!loading ? (
        <Card>
          <CardHeader title={tree.title} />
          <CardContent>
            <p>
              This will export all unique combinations standard and node ID in
              the tree as a CSV file. You must provide a grade and state for the
              standards.
            </p>
            <div>
              <TextField
                id="grade"
                label="Grade"
                value={grade}
                onChange={(e) => setGrade(e.target.value)}
                required
              />
            </div>
            <br />
            <div>
              <TextField
                id="state"
                label="State"
                value={state}
                onChange={(e) => setState(e.target.value)}
                required
              />
            </div>
          </CardContent>
          <CardActions>
            <Button
              variant="contained"
              color="primary"
              onClick={parseStandards}
              disabled={!grade || !state}
            >
              Parse Standards
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={exportStandardsAsCSV}
              disabled={!parsedStandards}
            >
              {parsedStandards
                ? `Export ${parsedStandards.length} lines`
                : 'Export'}
            </Button>
          </CardActions>
        </Card>
      ) : (
        <Container
          sx={{
            width: '100%',
            height: '200px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress variant="indeterminate" />
        </Container>
      )}
    </>
  )
}

function getAncestors(node: INode, nodeMap: NodeIdMap) {
  let currentNode = node
  const ancestors = []
  while (currentNode.parent_id) {
    currentNode = nodeMap[currentNode.parent_id].node
    ancestors.push(currentNode)
  }
  return ancestors
}
